import { useEffect, useRef } from 'react';
import { heroScenes } from '@/mocks/travel';

const WHATSAPP_NUMBER = '5585999325303';

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const titleRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const images = imageRefs.current.filter(Boolean) as HTMLDivElement[];
    const sceneCount = images.length;

    images.forEach((img, i) => {
      img.style.opacity = i === 0 ? '1' : '0';
      img.style.transform = 'scale(1)';
    });

    let rafId: number;

    const update = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      const totalScrollable = rect.height - window.innerHeight;
      if (totalScrollable <= 0) return;

      const progress = Math.max(0, Math.min(1, scrolled / totalScrollable));
      const rawScene = progress * (sceneCount - 1);
      const targetScene = Math.floor(rawScene);
      const blend = rawScene - targetScene;

      images.forEach((img, i) => {
        if (i === targetScene) {
          img.style.opacity = String(1 - blend * 0.25);
          img.style.transform = 'scale(1)';
        } else if (i === targetScene + 1) {
          img.style.opacity = String(blend);
          img.style.transform = `scale(${1.04 - blend * 0.04})`;
        } else {
          img.style.opacity = '0';
          img.style.transform = 'scale(1)';
        }
      });

      const fadeStart = 0.65;
      const textFade = progress > fadeStart
        ? Math.max(0, 1 - ((progress - fadeStart) / (1 - fadeStart)))
        : 1;

      if (titleRef.current) {
        titleRef.current.style.opacity = String(Math.max(0.1, textFade));
        titleRef.current.style.transform = `translateY(${(1 - textFade) * -35}px)`;
      }
      if (subtitleRef.current) {
        subtitleRef.current.style.opacity = String(Math.max(0, textFade - 0.05));
        subtitleRef.current.style.transform = `translateY(${(1 - textFade) * -20}px)`;
      }
      if (ctaRef.current) {
        ctaRef.current.style.opacity = String(Math.max(0, textFade - 0.1));
        ctaRef.current.style.transform = `translateY(${(1 - textFade) * -45}px)`;
      }
      if (indicatorRef.current) {
        indicatorRef.current.style.opacity = String(Math.max(0, 1 - progress * 4));
      }
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const setImageRef = (i: number) => (el: HTMLDivElement | null) => {
    imageRefs.current[i] = el;
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative w-full"
      style={{ height: '600vh' }}
    >
      <div className="sticky top-0 left-0 w-full overflow-hidden" style={{ height: '100vh' }}>
        {/* Dark overlay */}
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/50 via-black/20 to-black/55 pointer-events-none"></div>

        {/* Scene images */}
        {heroScenes.map((scene, index) => (
          <div
            key={index}
            ref={setImageRef(index)}
            className="absolute inset-0 w-full h-full"
            style={{ opacity: index === 0 ? 1 : 0, willChange: 'opacity, transform' }}
          >
            <img
              src={scene.image}
              alt={scene.title}
              className="w-full h-full object-cover object-center"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}

        {/* Text overlay */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 md:px-12">
          <div className="w-full max-w-3xl text-center">

            {/* Eyebrow */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-8 bg-white/30"></div>
              <span className="font-label text-[10px] sm:text-xs tracking-[0.25em] uppercase text-white/60">
                Ao Redor do Mundo Viagens
              </span>
              <div className="h-px w-8 bg-white/30"></div>
            </div>

            {/* Title — refined & harmonious */}
            <div
              ref={titleRef}
              style={{ willChange: 'transform, opacity' }}
            >
              <h1 className="font-heading font-light leading-[1.18] tracking-[-0.01em]">
                <span className="block text-white/90 text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] xl:text-[4rem]">
                  Sua próxima grande história
                </span>
                <span className="block text-white/90 text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] xl:text-[4rem]">
                  começa com uma viagem
                </span>
                <span
                  className="block italic text-[1.85rem] sm:text-[2.4rem] md:text-[3rem] lg:text-[3.5rem] xl:text-[4rem]"
                  style={{ color: '#ffcc55', textShadow: '0 0 32px rgba(255,180,40,0.55), 0 2px 8px rgba(0,0,0,0.4)' }}
                >
                  inesquecível.
                </span>
              </h1>
            </div>

            <p
              ref={subtitleRef}
              className="text-white/65 text-sm md:text-base font-light max-w-md mx-auto mt-6 mb-10 leading-relaxed tracking-wide"
              style={{ willChange: 'transform, opacity' }}
            >
              Há mais de 12 anos criando viagens e intercâmbios personalizados para quem valoriza experiências únicas ao redor do mundo.
            </p>

            <div
              ref={ctaRef}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
              style={{ willChange: 'transform, opacity' }}
            >
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de planejar minha viagem dos sonhos.`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 px-7 py-3.5 bg-white text-primary-900 rounded-full text-sm font-medium tracking-wide hover:bg-white/95 transition-all duration-500 whitespace-nowrap cursor-pointer"
              >
                Planejar minha viagem
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300 text-sm"></i>
              </a>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de falar com um especialista.`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 px-7 py-3.5 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-full text-sm font-light tracking-wide hover:bg-white/18 transition-all duration-500 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                Falar com um especialista
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          ref={indicatorRef}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        >
          <span className="text-white/40 text-[10px] tracking-[0.2em] font-label uppercase">
            Role para explorar
          </span>
          <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center p-1">
            <div className="w-0.5 h-1.5 bg-white/50 rounded-full animate-scroll-indicator"></div>
          </div>
        </div>
      </div>
    </section>
  );
}